import { useEffect, useState, useCallback, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { MapContainer, TileLayer, Marker, Polyline, useMapEvents, useMap } from 'react-leaflet'
import L from 'leaflet'
import { AppShell } from '../components/layout/AppShell'
import { orgService } from '../services/orgService'

import type { CourseLeg, Race } from '../types/index'

// ── Point type ────────────────────────────────────────────────────────────────

interface LatLng { lat: number; lng: number }
interface Pt extends LatLng { id: number; elevM?: number }

let _nextId = 1
const newPt = (lat: number, lng: number, elevM?: number): Pt => ({ id: _nextId++, lat, lng, elevM })

// ── Elevation helpers ─────────────────────────────────────────────────────────

async function fetchMissingElevations(pts: Pt[]): Promise<Pt[]> {
  const missing = pts.map((p, i) => ({ i, p })).filter(({ p }) => p.elevM === undefined)
  if (missing.length === 0) return pts

  const BATCH = 512
  const updated = [...pts]

  for (let b = 0; b < missing.length; b += BATCH) {
    const chunk = missing.slice(b, b + BATCH)
    const locations = chunk.map(({ p }) => ({ latitude: p.lat, longitude: p.lng }))
    const res = await fetch('https://api.open-elevation.com/api/v1/lookup', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ locations }),
    })
    if (!res.ok) throw new Error('Elevation API error')
    const data = await res.json()
    data.results.forEach((r: { elevation: number }, j: number) => {
      updated[chunk[j].i] = { ...updated[chunk[j].i], elevM: r.elevation }
    })
  }
  return updated
}

function calcElevGainLoss(pts: Pt[]): { gainFt: number; lossFt: number; rangeFt: number } {
  // Extract only points that have elevation data
  const elevs = pts.map(p => p.elevM).filter((e): e is number => e !== undefined)
  if (elevs.length < 2) return { gainFt: 0, lossFt: 0, rangeFt: 0 }

  // Smooth with a moving average window (window size scales with point count)
  // Larger windows suppress API noise better at high point densities
  const WINDOW = Math.max(5, Math.round(elevs.length / 20))
  const smoothed: number[] = []
  for (let i = 0; i < elevs.length; i++) {
    const start = Math.max(0, i - Math.floor(WINDOW / 2))
    const end = Math.min(elevs.length - 1, i + Math.floor(WINDOW / 2))
    let sum = 0
    for (let j = start; j <= end; j++) sum += elevs[j]
    smoothed.push(sum / (end - start + 1))
  }

  // Calculate gain/loss from smoothed profile with a 10m threshold
  const THRESHOLD_M = 10
  let gain = 0, loss = 0
  for (let i = 1; i < smoothed.length; i++) {
    const diff = smoothed[i] - smoothed[i - 1]
    if (diff > THRESHOLD_M / WINDOW) gain += diff
    else if (diff < -(THRESHOLD_M / WINDOW)) loss += Math.abs(diff)
  }

  const M_TO_FT = 3.28084
  const rangeFt = Math.round((Math.max(...elevs) - Math.min(...elevs)) * M_TO_FT)
  return { gainFt: Math.round(gain * M_TO_FT), lossFt: Math.round(loss * M_TO_FT), rangeFt }
}

// ── Geometry helpers ──────────────────────────────────────────────────────────

function distM(a: LatLng, b: LatLng): number {
  const R = 6371000
  const dLat = (b.lat - a.lat) * Math.PI / 180
  const dLng = (b.lng - a.lng) * Math.PI / 180
  const sin2 = Math.sin(dLat / 2) ** 2 + Math.cos(a.lat * Math.PI / 180) * Math.cos(b.lat * Math.PI / 180) * Math.sin(dLng / 2) ** 2
  return R * 2 * Math.atan2(Math.sqrt(sin2), Math.sqrt(1 - sin2))
}

function totalDistMi(pts: LatLng[]): number {
  let d = 0
  for (let i = 1; i < pts.length; i++) d += distM(pts[i - 1], pts[i])
  return d / 1609.344
}

function nearestSegmentIdx(pts: LatLng[], p: LatLng): number {
  let best = 0, bestD = Infinity
  for (let i = 0; i < pts.length - 1; i++) {
    const mid: LatLng = { lat: (pts[i].lat + pts[i + 1].lat) / 2, lng: (pts[i].lng + pts[i + 1].lng) / 2 }
    const d = distM(mid, p)
    if (d < bestD) { bestD = d; best = i }
  }
  return best + 1
}

// ── Icons ─────────────────────────────────────────────────────────────────────

const startIcon = L.divIcon({
  className: '',
  html: `<div style="width:18px;height:18px;border-radius:50%;background:#22c55e;border:2px solid white;box-shadow:0 1px 5px rgba(0,0,0,0.5);cursor:grab"></div>`,
  iconAnchor: [9, 9],
})
const endIcon = L.divIcon({
  className: '',
  html: `<div style="width:18px;height:18px;border-radius:50%;background:#ef4444;border:2px solid white;box-shadow:0 1px 5px rgba(0,0,0,0.5);cursor:grab"></div>`,
  iconAnchor: [9, 9],
})
const waypointIcon = (selected: boolean) => L.divIcon({
  className: '',
  html: `<div style="width:13px;height:13px;border-radius:50%;background:${selected ? '#f59e0b' : '#6366f1'};border:2px solid white;box-shadow:0 1px 4px rgba(0,0,0,0.4);cursor:grab"></div>`,
  iconAnchor: [6, 6],
})
const adjacentIcon = L.divIcon({
  className: '',
  html: `<div style="width:10px;height:10px;border-radius:50%;background:#94a3b8;border:1.5px solid white;opacity:0.7"></div>`,
  iconAnchor: [5, 5],
})
const xpEditorIcon = (label: string | number, isActive: boolean, isSelected: boolean) => {
  const s = String(label)
  const fontSize = s.length > 2 ? '10px' : s.length > 1 ? '12px' : '14px'
  return L.divIcon({
    className: '',
    html: `<div style="background:${isSelected ? '#f59e0b' : isActive ? '#22c55e' : '#1e293b'};color:white;border-radius:50%;width:34px;height:34px;display:flex;align-items:center;justify-content:center;font-size:${fontSize};font-weight:800;border:${isSelected ? '2.5px solid #92400e' : '2.5px solid white'};box-shadow:0 2px 8px rgba(0,0,0,0.6);letter-spacing:-0.5px;cursor:grab">${s}</div>`,
    iconSize: [34, 34],
    iconAnchor: [17, 17],
  })
}

// ── Undo history ──────────────────────────────────────────────────────────────

interface HistoryEntry { pts: Pt[]; vanPts: Pt[] }

// ── Map helpers ───────────────────────────────────────────────────────────────

function MapClickHandler({ onAdd, enabled }: { onAdd: (p: LatLng) => void; enabled: boolean }) {
  useMapEvents({ click(e) { if (enabled) onAdd({ lat: e.latlng.lat, lng: e.latlng.lng }) } })
  return null
}

// ── Lasso overlay — draws a rubber-band rect and reports the screen bbox ──────

interface LassoProps {
  enabled: boolean
  onSelect: (minLat: number, maxLat: number, minLng: number, maxLng: number) => void
}

function LassoOverlay({ enabled, onSelect }: LassoProps) {
  const map = useMap()
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const startRef = useRef<{ x: number; y: number } | null>(null)
  const currentRef = useRef<{ x: number; y: number } | null>(null)

  useEffect(() => {
    if (!enabled) {
      if (canvasRef.current) canvasRef.current.style.display = 'none'
      return
    }

    const container = map.getContainer()

    if (!canvasRef.current) {
      const c = document.createElement('canvas')
      c.style.cssText = 'position:absolute;top:0;left:0;width:100%;height:100%;z-index:1500;cursor:crosshair;pointer-events:all'
      container.appendChild(c)
      canvasRef.current = c
    }
    const canvas = canvasRef.current
    canvas.style.display = 'block'
    canvas.width = container.offsetWidth
    canvas.height = container.offsetHeight

    const ctx = canvas.getContext('2d')!

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      if (!startRef.current || !currentRef.current) return
      const { x: x1, y: y1 } = startRef.current
      const { x: x2, y: y2 } = currentRef.current
      ctx.strokeStyle = '#f59e0b'
      ctx.lineWidth = 2
      ctx.setLineDash([6, 3])
      ctx.fillStyle = 'rgba(245,158,11,0.1)'
      ctx.beginPath()
      ctx.rect(Math.min(x1, x2), Math.min(y1, y2), Math.abs(x2 - x1), Math.abs(y2 - y1))
      ctx.fill()
      ctx.stroke()
    }

    const onMouseDown = (e: MouseEvent) => {
      const r = canvas.getBoundingClientRect()
      startRef.current = { x: e.clientX - r.left, y: e.clientY - r.top }
      currentRef.current = { ...startRef.current }
      map.dragging.disable()
    }

    const onMouseMove = (e: MouseEvent) => {
      if (!startRef.current) return
      const r = canvas.getBoundingClientRect()
      currentRef.current = { x: e.clientX - r.left, y: e.clientY - r.top }
      draw()
    }

    const onMouseUp = () => {
      if (!startRef.current || !currentRef.current) return
      map.dragging.enable()
      const { x: x1, y: y1 } = startRef.current
      const { x: x2, y: y2 } = currentRef.current
      const pxBounds = L.bounds(
        [Math.min(x1, x2), Math.min(y1, y2)],
        [Math.max(x1, x2), Math.max(y1, y2)]
      )
      // Convert pixel corners to lat/lng
      const sw = map.containerPointToLatLng([pxBounds.min!.x, pxBounds.max!.y])
      const ne = map.containerPointToLatLng([pxBounds.max!.x, pxBounds.min!.y])
      startRef.current = null
      currentRef.current = null
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      if (Math.abs(x2 - x1) > 5 && Math.abs(y2 - y1) > 5) {
        onSelect(sw.lat, ne.lat, sw.lng, ne.lng)
      }
    }

    canvas.addEventListener('mousedown', onMouseDown)
    canvas.addEventListener('mousemove', onMouseMove)
    canvas.addEventListener('mouseup', onMouseUp)
    return () => {
      canvas.removeEventListener('mousedown', onMouseDown)
      canvas.removeEventListener('mousemove', onMouseMove)
      canvas.removeEventListener('mouseup', onMouseUp)
      if (canvasRef.current) { canvasRef.current.style.display = 'none' }
      map.dragging.enable()
    }
  }, [enabled, map, onSelect])

  return null
}

function FitBounds({ pts, trigger }: { pts: Pt[]; trigger: number }) {
  const map = useMap()
  useEffect(() => {
    if (pts.length < 2) return
    map.invalidateSize()
    map.fitBounds(L.latLngBounds(pts.map(p => [p.lat, p.lng])), { padding: [40, 40] })
  }, [trigger]) // eslint-disable-line react-hooks/exhaustive-deps
  return null
}

// ── Difficulty badge ──────────────────────────────────────────────────────────

function DiffBadge({ d }: { d: string }) {
  const cls = d === 'E' ? 'bg-green-100 text-green-700' : d === 'M' ? 'bg-yellow-100 text-yellow-700' : d === 'MC' ? 'bg-orange-100 text-orange-700' : 'bg-red-100 text-red-700'
  return <span className={`px-1.5 py-0.5 rounded text-xs font-bold ${cls}`}>{d}</span>
}

// ── Main component ─────────────────────────────────────────────────────────────

export function OrgLegEditorPage() {
  const { raceId, legId } = useParams<{ raceId: string; legId: string }>()
  const navigate = useNavigate()
  const numRaceId = Number(raceId)

  // Race metadata
  const [race, setRace] = useState<Race | null>(null)

  // All course legs for this race
  const [allLegs, setAllLegs] = useState<CourseLeg[]>([])
  const [activeLegId, setActiveLegId] = useState<number>(Number(legId))
  const [prevLegWps, setPrevLegWps] = useState<LatLng[]>([])
  const [nextLegWps, setNextLegWps] = useState<LatLng[]>([])

  // Active leg editor state
  const [pts, setPts] = useState<Pt[]>([])
  const [selectedIdx, setSelectedIdx] = useState<number | null>(null)
  // Van route state (separate from runner route)
  const [vanPts, setVanPts] = useState<Pt[]>([])
  const [vanSelectedIdx, setVanSelectedIdx] = useState<number | null>(null)

  const [activeLayer, setActiveLayer] = useState<'runner' | 'van'>('runner')
  const [mode, setMode] = useState<'select' | 'add' | 'delete' | 'lasso'>('select')
  const modeRef = useRef<'select' | 'add' | 'delete' | 'lasso'>('select')

  // Undo history — push before any destructive edit
  const [history, setHistory] = useState<HistoryEntry[]>([])
  const pushHistory = useCallback((currentPts: Pt[], currentVanPts: Pt[]) => {
    setHistory(prev => [...prev.slice(-19), { pts: currentPts, vanPts: currentVanPts }])
  }, [])
  const [legNumberInput, setLegNumberInput] = useState<string>('1')
  const [difficultyRating, setDifficultyRating] = useState('M')
  const [vanTransfer, setVanTransfer] = useState(false)
  const [instructions, setInstructions] = useState('')
  const [xpInstructions, setXpInstructions] = useState('')
  const [elevStats, setElevStats] = useState<{ gainFt: number; lossFt: number; rangeFt?: number } | null>(null)
  const [fitTrigger, setFitTrigger] = useState(0)

  // XP editing — selectedXpLegNum is the leg whose *end* is this XP (0 = Start/Finish = leg 1 start)
  const [selectedXpLegNum, setSelectedXpLegNum] = useState<number | null>(null)
  const [xpEditNote, setXpEditNote] = useState('')
  const [xpSaving, setXpSaving] = useState(false)
  const [xpDeleting, setXpDeleting] = useState(false)
  const [xpAdding, setXpAdding] = useState(false)

  // UI state
  const [loading, setLoading] = useState(true)
  const [legLoading, setLegLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [fetchingElev, setFetchingElev] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [unsaved, setUnsaved] = useState(false)
  const [panelOpen, setPanelOpen] = useState(true)

  // Warn before leaving with unsaved changes
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => { if (unsaved) e.preventDefault() }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [unsaved])

  // ── Load all legs on mount ────────────────────────────────────────────────────

  useEffect(() => {
    Promise.all([orgService.getCourseLegs(numRaceId), orgService.getRace(numRaceId)])
      .then(([legs, r]) => {
        setAllLegs(legs)
        setRace(r)
        setLoading(false)
        loadLeg(Number(legId), legs)
      })
      .catch(() => { setError('Failed to load course'); setLoading(false) })
  }, [numRaceId]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Load a specific leg's waypoints ──────────────────────────────────────────

  const loadLeg = useCallback(async (id: number, legs?: CourseLeg[]) => {
    const legList = legs ?? allLegs
    const found = legList.find(l => l.id === id)
    if (!found) return

    setLegLoading(true)
    setSelectedIdx(null)
    setVanSelectedIdx(null)
    setMode('select')
    modeRef.current = 'select'
    setActiveLayer('runner')
    setError(null)

    try {
      const prev = legList.find(l => l.legNumber === found.legNumber - 1) ?? null
      const next = legList.find(l => l.legNumber === found.legNumber + 1) ?? null
      const [wps, vanRoute, prevWps, nextWps] = await Promise.all([
        orgService.getWaypoints(id),
        orgService.getVanRoute(id),
        prev ? orgService.getWaypoints(prev.id) : Promise.resolve([]),
        next ? orgService.getWaypoints(next.id) : Promise.resolve([]),
      ])
      setActiveLegId(id)
      setLegNumberInput(String(found.legNumber))
      setDifficultyRating(found.difficultyRating)
      setVanTransfer(found.vanTransfer ?? false)
      setInstructions(found.instructions ?? '')
      setXpInstructions(found.xpInstructions ?? '')
      setElevStats({ gainFt: found.elevationGain ?? 0, lossFt: found.elevationLoss ?? 0 })
      setUnsaved(false)
      setSaved(false)

      if (wps.length > 0) {
        setPts(wps.map(w => newPt(w.latitude, w.longitude, w.elevationM ?? undefined)))
      } else {
        setPts([newPt(found.startLat, found.startLng), newPt(found.endLat, found.endLng)])
      }
      // Van route — use DB if available, else bare start/end
      if (vanRoute.length > 0) {
        setVanPts(vanRoute.map(p => newPt(p.latitude, p.longitude)))
      } else {
        setVanPts([newPt(found.startLat, found.startLng), newPt(found.endLat, found.endLng)])
      }

      // Adjacent leg previews — use DB waypoints if available, else fall back to start/end
      setPrevLegWps(prevWps.length > 0
        ? prevWps.map(w => ({ lat: w.latitude, lng: w.longitude }))
        : prev ? [{ lat: prev.startLat, lng: prev.startLng }, { lat: prev.endLat, lng: prev.endLng }] : [])
      setNextLegWps(nextWps.length > 0
        ? nextWps.map(w => ({ lat: w.latitude, lng: w.longitude }))
        : next ? [{ lat: next.startLat, lng: next.startLng }, { lat: next.endLat, lng: next.endLng }] : [])

      setFitTrigger(t => t + 1)
    } catch {
      setError('Failed to load leg waypoints')
    } finally {
      setLegLoading(false)
    }
  }, [allLegs])

  const handleSelectLeg = (id: number) => {
    if (unsaved) {
      if (!window.confirm('You have unsaved changes. Switch legs anyway?')) return
    }
    loadLeg(id)
  }

  // ── Derived from active leg ───────────────────────────────────────────────────

  const activeLeg = allLegs.find(l => l.id === activeLegId) ?? null
  const nextLeg = activeLeg ? allLegs.find(l => l.legNumber === activeLeg.legNumber + 1) ?? null : null
  const prevLeg = activeLeg ? allLegs.find(l => l.legNumber === activeLeg.legNumber - 1) ?? null : null

  const startPt = pts[0]
  const endPt = pts[pts.length - 1]
  const distMi = pts.length >= 2 ? totalDistMi(pts) : 0

  // Van transfer on active leg means its end and next leg's start are intentionally different
  const endMatchesNext = activeLeg?.vanTransfer ? true
    : nextLeg && endPt ? distM(endPt, { lat: nextLeg.startLat, lng: nextLeg.startLng }) < 20 : true
  // Van transfer on prev leg means its end and our start are intentionally different
  const startMatchesPrev = prevLeg?.vanTransfer ? true
    : prevLeg && startPt ? distM(startPt, { lat: prevLeg.endLat, lng: prevLeg.endLng }) < 20 : true

  // Check alignment for all legs (for the side panel warning badges) — skip van transfer gaps
  const alignmentWarnings = new Set<number>()
  for (let i = 0; i < allLegs.length - 1; i++) {
    const a = allLegs[i], b = allLegs[i + 1]
    if (!a.vanTransfer && distM({ lat: a.endLat, lng: a.endLng }, { lat: b.startLat, lng: b.startLng }) > 20) {
      alignmentWarnings.add(a.legNumber)
      alignmentWarnings.add(b.legNumber)
    }
  }

  const nextLegPts: LatLng[] = nextLeg ? nextLegWps : []
  const prevLegPts: LatLng[] = prevLeg ? prevLegWps : []

  // ── Point manipulation ────────────────────────────────────────────────────────

  const activeLayerRef = useRef<'runner' | 'van'>('runner')

  const handleMapClick = useCallback((p: LatLng) => {
    if (modeRef.current !== 'add') return
    const pt = newPt(p.lat, p.lng)
    if (activeLayerRef.current === 'van') {
      setVanPts(prev => {
        pushHistory(pts, prev)
        if (prev.length < 2) return [...prev, pt]
        const insertAt = nearestSegmentIdx(prev, p)
        const next = [...prev]
        next.splice(insertAt, 0, pt)
        return next
      })
    } else {
      setPts(prev => {
        pushHistory(prev, vanPts)
        if (prev.length < 2) return [...prev, pt]
        const insertAt = nearestSegmentIdx(prev, p)
        const next = [...prev]
        next.splice(insertAt, 0, pt)
        return next
      })
    }
    setUnsaved(true)
  }, [pts, vanPts, pushHistory])

  const handleMarkerDrag = useCallback((idx: number, e: L.DragEndEvent) => {
    const { lat, lng } = e.target.getLatLng()
    setPts(prev => prev.map((p, i) => i === idx ? { ...p, lat, lng, elevM: undefined } : p))
    setUnsaved(true)
  }, [])

  const handleVanMarkerDrag = useCallback((idx: number, e: L.DragEndEvent) => {
    const { lat, lng } = e.target.getLatLng()
    setVanPts(prev => prev.map((p, i) => i === idx ? { ...p, lat, lng } : p))
    setUnsaved(true)
  }, [])

  const handleVanMarkerClick = useCallback((idx: number) => {
    if (modeRef.current === 'delete') {
      setVanPts(prev => {
        if (idx === 0 || idx === prev.length - 1) return prev
        pushHistory(pts, prev)
        return prev.filter((_, i) => i !== idx)
      })
      setVanSelectedIdx(null)
      setUnsaved(true)
    } else {
      setVanSelectedIdx(prev => prev === idx ? null : idx)
    }
  }, [pts, pushHistory])

  const handleMarkerClick = useCallback((idx: number) => {
    if (modeRef.current === 'delete') {
      setPts(prev => {
        if (idx === 0 || idx === prev.length - 1) return prev
        pushHistory(prev, vanPts)
        return prev.filter((_, i) => i !== idx)
      })
      setSelectedIdx(null)
      setUnsaved(true)
    } else {
      setSelectedIdx(prev => prev === idx ? null : idx)
    }
  }, [vanPts, pushHistory])

  // ── XP drag / select ──────────────────────────────────────────────────────────

  const handleXpClick = useCallback((legNum: number, xpNote: string) => {
    setSelectedXpLegNum(prev => prev === legNum ? null : legNum)
    setXpEditNote(xpNote)
    setSelectedIdx(null)
    setVanSelectedIdx(null)
  }, [])

  // Dragging XP N (end of leg N + start of leg N+1) — for normal XPs and the "a" side of van transfers
  const handleXpDrag = useCallback((legNum: number, e: L.DragEndEvent) => {
    const { lat, lng } = e.target.getLatLng()
    setAllLegs(prev => prev.map(l => {
      if (l.legNumber === legNum) return { ...l, endLat: lat, endLng: lng }
      // Only co-move the next leg's start if NOT a van transfer (they're independent locations)
      if (l.legNumber === legNum + 1 && !prev.find(x => x.legNumber === legNum)?.vanTransfer)
        return { ...l, startLat: lat, startLng: lng }
      return l
    }))
    setPts(prev => {
      const al = allLegs.find(l => l.id === activeLegId)
      if (!al) return prev
      if (al.legNumber === legNum)
        return [...prev.slice(0, -1), newPt(lat, lng, prev[prev.length - 1]?.elevM)]
      if (al.legNumber === legNum + 1 && !allLegs.find(x => x.legNumber === legNum)?.vanTransfer)
        return [newPt(lat, lng, prev[0]?.elevM), ...prev.slice(1)]
      return prev
    })
    setUnsaved(true)
  }, [allLegs, activeLegId])

  // Dragging XP Nb (start of leg N, after a van transfer on leg N-1) — moves only that leg's start
  const handleXpDragStart = useCallback((legNum: number, e: L.DragEndEvent) => {
    const { lat, lng } = e.target.getLatLng()
    setAllLegs(prev => prev.map(l =>
      l.legNumber === legNum ? { ...l, startLat: lat, startLng: lng } : l
    ))
    setPts(prev => {
      const al = allLegs.find(l => l.id === activeLegId)
      if (al?.legNumber === legNum)
        return [newPt(lat, lng, prev[0]?.elevM), ...prev.slice(1)]
      return prev
    })
    setUnsaved(true)
  }, [allLegs, activeLegId])

  // Save the dragged XP position to the server
  // legNum > 0: normal XP (end of legNum, start of legNum+1 if not van transfer)
  // legNum < 0: van transfer "b" side (start of abs(legNum))
  const handleXpSave = useCallback(async (legNum: number) => {
    setXpSaving(true)
    try {
      if (legNum < 0) {
        // Xb side — only save start of the leg
        const legB = allLegs.find(l => l.legNumber === Math.abs(legNum))
        if (!legB) return
        await orgService.updateCourseLeg(legB.id, {
          startLat: legB.startLat, startLng: legB.startLng,
          xpInstructions: xpEditNote.trim() || null,
        })
        setAllLegs(prev => prev.map(l => l.legNumber === Math.abs(legNum) ? { ...l, xpInstructions: xpEditNote.trim() || null } : l))
      } else {
        const legA = allLegs.find(l => l.legNumber === legNum)
        const legB = allLegs.find(l => l.legNumber === legNum + 1)
        const isVanTransfer = legA?.vanTransfer ?? false
        const saves: Promise<any>[] = []
        if (legA) saves.push(orgService.updateCourseLeg(legA.id, {
          endLat: legA.endLat, endLng: legA.endLng,
          xpInstructions: xpEditNote.trim() || null,
        }))
        // Only co-save next leg's start if not a van transfer (independent location)
        if (legB && !isVanTransfer) saves.push(orgService.updateCourseLeg(legB.id, {
          startLat: legB.startLat, startLng: legB.startLng,
        }))
        await Promise.all(saves)
        if (legA) setAllLegs(prev => prev.map(l => l.legNumber === legNum ? { ...l, xpInstructions: xpEditNote.trim() || null } : l))
      }
      setUnsaved(false)
    } catch {
      setError('Failed to save XP')
    } finally {
      setXpSaving(false)
    }
  }, [allLegs, xpEditNote])

  // ── Lasso select to delete ────────────────────────────────────────────────────

  const handleLassoSelect = useCallback((minLat: number, maxLat: number, minLng: number, maxLng: number) => {
    const inBox = (lat: number, lng: number) => lat >= minLat && lat <= maxLat && lng >= minLng && lng <= maxLng

    // Delete intermediate waypoints inside the box for the active layer only (never start/end)
    const newPts = activeLayer === 'runner'
      ? pts.filter((p, i) => i === 0 || i === pts.length - 1 || !inBox(p.lat, p.lng))
      : pts
    const newVanPts = activeLayer === 'van'
      ? vanPts.filter((p, i) => i === 0 || i === vanPts.length - 1 || !inBox(p.lat, p.lng))
      : vanPts

    const totalChanges = (pts.length - newPts.length) + (vanPts.length - newVanPts.length)
    if (totalChanges === 0) return

    const msg = [
      pts.length - newPts.length > 0 && `${pts.length - newPts.length} runner waypoint(s)`,
      vanPts.length - newVanPts.length > 0 && `${vanPts.length - newVanPts.length} van waypoint(s)`,
    ].filter(Boolean).join(', ')

    if (!window.confirm(`Delete selected: ${msg}?`)) return

    pushHistory(pts, vanPts)
    if (newPts.length !== pts.length) { setPts(newPts); setUnsaved(true) }
    if (newVanPts.length !== vanPts.length) { setVanPts(newVanPts); setUnsaved(true) }

    setMode('select')
    modeRef.current = 'select'
  }, [pts, vanPts, activeLayer, pushHistory])

  // ── Undo ──────────────────────────────────────────────────────────────────────

  const handleUndo = useCallback(() => {
    setHistory(prev => {
      if (prev.length === 0) return prev
      const last = prev[prev.length - 1]
      setPts(last.pts)
      setVanPts(last.vanPts)
      setUnsaved(true)
      return prev.slice(0, -1)
    })
  }, [])

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
        e.preventDefault()
        handleUndo()
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [handleUndo])

  const handleDeleteXp = useCallback(async (legNum: number) => {
    const legToDelete = allLegs.find(l => l.legNumber === legNum)
    if (!legToDelete) return
    if (!window.confirm(`Delete Leg ${legNum}?\n\nThis permanently deletes the runner route, van route, all waypoints, and XP notes for this leg. Remaining legs keep their current numbers.`)) return
    pushHistory(pts, vanPts)
    setXpDeleting(true)
    try {
      await orgService.deleteCourseLeg(legToDelete.id)
      const updatedLegs = await orgService.getCourseLegs(numRaceId)
      setAllLegs(updatedLegs)
      setHistory([]) // committed to DB — clear undo stack to avoid stale leg IDs
      setSelectedXpLegNum(null)
      // If we deleted the active leg, switch to the nearest remaining leg
      if (activeLegId === legToDelete.id) {
        const nearest = updatedLegs.find(l => l.legNumber >= legNum) ?? updatedLegs[updatedLegs.length - 1]
        if (nearest) loadLeg(nearest.id, updatedLegs)
      }
    } catch {
      setError('Failed to delete XP')
    } finally {
      setXpDeleting(false)
    }
  }, [allLegs, activeLegId, numRaceId, loadLeg, pts, vanPts, pushHistory])

  const handleAddXp = useCallback(async (afterLegNum: number) => {
    const legA = allLegs.find(l => l.legNumber === afterLegNum)
    const legB = allLegs.find(l => l.legNumber === afterLegNum + 1)
    if (!legA) return
    // Place the new XP midway between legA end and legB start (or nudge if last leg)
    const newEndLat = legB ? (legA.endLat + legB.startLat) / 2 : legA.endLat + (legA.endLat - legA.startLat) * 0.1
    const newEndLng = legB ? (legA.endLng + legB.startLng) / 2 : legA.endLng + (legA.endLng - legA.startLng) * 0.1
    pushHistory(pts, vanPts)
    setXpAdding(true)
    try {
      const newLeg = await orgService.createCourseLeg(numRaceId, {
        afterLegNumber: afterLegNum,
        startLat: legA.endLat, startLng: legA.endLng,
        endLat: newEndLat, endLng: newEndLng,
      })
      const updatedLegs = await orgService.getCourseLegs(numRaceId)
      setAllLegs(updatedLegs)
      setHistory([]) // committed to DB — clear undo stack to avoid stale leg IDs
      setSelectedXpLegNum(null)
      loadLeg(newLeg.id, updatedLegs)
    } catch {
      setError('Failed to add XP')
    } finally {
      setXpAdding(false)
    }
  }, [allLegs, numRaceId, loadLeg, pts, vanPts, pushHistory])

  // ── Save ──────────────────────────────────────────────────────────────────────

  const handleSave = async () => {
    if (!activeLeg || pts.length < 2) return
    if (!allLegs.some(l => l.id === activeLegId)) return
    setError(null)

    let finalPts = pts
    const missingCount = pts.filter(p => p.elevM === undefined).length
    if (missingCount > 0) {
      setFetchingElev(true)
      try {
        finalPts = await fetchMissingElevations(pts)
        setPts(finalPts)
      } catch {
        setError(`Elevation fetch failed for ${missingCount} points — saving without elevation data`)
        finalPts = pts
      } finally {
        setFetchingElev(false)
      }
    }

    const { gainFt, lossFt } = calcElevGainLoss(finalPts)
    setElevStats({ gainFt, lossFt })

    setSaving(true)
    try {
      const newLegNumber = parseInt(legNumberInput, 10)
      await orgService.updateCourseLeg(activeLegId, {
        legNumber: isNaN(newLegNumber) ? undefined : newLegNumber,
        startLat: startPt.lat, startLng: startPt.lng,
        endLat: endPt.lat, endLng: endPt.lng,
        distanceMiles: parseFloat(distMi.toFixed(2)),
        difficultyRating: difficultyRating as any,
        elevationGain: gainFt,
        elevationLoss: lossFt,
        instructions: instructions.trim() || null,
        xpInstructions: xpInstructions.trim() || null,
        vanTransfer,
      })
      await orgService.replaceWaypoints(activeLegId, finalPts.map(p => ({
        latitude: p.lat, longitude: p.lng, elevationM: p.elevM,
      })))

      // Save van route (no elevation needed)
      if (vanPts.length >= 2) {
        await orgService.replaceVanRoute(activeLegId, vanPts.map(p => ({ latitude: p.lat, longitude: p.lng })))
      }

      // Update local allLegs so the panel reflects the new distance/difficulty/number immediately
      const savedLegNumber = isNaN(newLegNumber) ? activeLeg.legNumber : newLegNumber
      setAllLegs(prev => prev
        .map(l => l.id === activeLegId
          ? { ...l, legNumber: savedLegNumber, distanceMiles: parseFloat(distMi.toFixed(2)), difficultyRating: difficultyRating as any, elevationGain: gainFt, elevationLoss: lossFt, instructions: instructions.trim() || null, xpInstructions: xpInstructions.trim() || null, vanTransfer }
          : l
        )
        .sort((a, b) => a.legNumber - b.legNumber)
      )

      setSaved(true)
      setUnsaved(false)
      setTimeout(() => setSaved(false), 2000)
    } catch {
      setError('Failed to save changes')
    } finally {
      setSaving(false)
    }
  }

  // ── Render ────────────────────────────────────────────────────────────────────

  if (loading) return (
    <AppShell><div className="h-full flex items-center justify-center text-gray-400">Loading course...</div></AppShell>
  )

  const polyLine: [number, number][] = pts.map(p => [p.lat, p.lng])
  const missingElev = pts.filter(p => p.elevM === undefined).length

  return (
    <AppShell>
      <div className="h-full flex flex-col overflow-hidden pb-16">

        {/* ── Top bar ── */}
        <div className="px-4 pt-2 pb-2 flex-shrink-0 border-b border-gray-200 bg-white flex items-center gap-3">
          <button onClick={() => navigate(`/org/races/${raceId}?tab=course`)} className="text-xs text-indigo-600 flex items-center gap-1 flex-shrink-0">
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
            Back
          </button>

          <div className="flex-1 min-w-0">
            <h1 className="text-sm font-bold leading-tight truncate">
              {activeLeg ? `Leg ${activeLeg.legNumber} — GPX Editor` : 'GPX Editor'}
              {race && <span className="ml-2 text-xs font-normal text-gray-400">{race.name}</span>}
              {unsaved && <span className="ml-2 text-xs text-amber-500 font-normal">● unsaved</span>}
            </h1>
            <p className="text-xs text-gray-500 leading-tight">
              {distMi.toFixed(2)} mi · {pts.length} pts · <span className="font-medium text-gray-600">{allLegs.length} XPs</span>
              {elevStats && <span className="ml-2"><span className="text-green-700">↑{elevStats.gainFt.toLocaleString()} ft</span>{' '}<span className="text-orange-700">↓{elevStats.lossFt.toLocaleString()} ft</span>{elevStats.rangeFt !== undefined && <span className="text-gray-500"> · range {elevStats.rangeFt.toLocaleString()} ft</span>}</span>}
              {missingElev > 0 && <span className="ml-2 text-amber-500">{missingElev} pts missing elev</span>}
              {!endMatchesNext && nextLeg && <span className="ml-2 text-amber-600 font-semibold">⚠ End ≠ Leg {nextLeg.legNumber}</span>}
              {!startMatchesPrev && prevLeg && <span className="ml-2 text-amber-600 font-semibold">⚠ Start ≠ Leg {prevLeg.legNumber}</span>}
            </p>
          </div>

          {/* Prev / Next buttons */}
          <div className="flex gap-1 flex-shrink-0">
            <button
              onClick={() => prevLeg && handleSelectLeg(prevLeg.id)}
              disabled={!prevLeg}
              className="px-2 py-1 text-xs rounded border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-30"
              title="Previous leg"
            >← Leg {activeLeg ? activeLeg.legNumber - 1 : ''}</button>
            <button
              onClick={() => nextLeg && handleSelectLeg(nextLeg.id)}
              disabled={!nextLeg}
              className="px-2 py-1 text-xs rounded border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-30"
              title="Next leg"
            >Leg {activeLeg ? activeLeg.legNumber + 1 : ''} →</button>
          </div>

          <button
            onClick={handleSave}
            disabled={saving || fetchingElev || pts.length < 2}
            className={`flex-shrink-0 px-3 py-1.5 text-sm rounded-lg font-medium transition-colors ${saved ? 'bg-green-600 text-white' : 'bg-indigo-600 text-white hover:bg-indigo-700'} disabled:opacity-40`}
          >
            {fetchingElev ? 'Fetching elev…' : saving ? 'Saving…' : saved ? '✓ Saved' : 'Save'}
          </button>
        </div>

        {error && (
          <div className="mx-3 mt-1 bg-red-50 border border-red-200 text-red-700 text-xs px-3 py-1.5 rounded flex-shrink-0">{error}</div>
        )}

        {/* ── Toolbar ── */}
        <div className="flex items-center gap-2 px-3 py-1.5 flex-shrink-0 bg-gray-50 border-b border-gray-200">
          <button
            onClick={() => setPanelOpen(o => !o)}
            className="px-2 py-1 text-xs rounded border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 flex items-center gap-1"
            title={panelOpen ? 'Hide leg list' : 'Show leg list'}
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
            {panelOpen ? 'Hide' : 'Legs'}
          </button>

          <div className="w-px h-4 bg-gray-300" />

          {/* Layer toggle */}
          <div className="flex rounded-md border border-gray-300 overflow-hidden text-xs">
            <button
              onClick={() => { setActiveLayer('runner'); activeLayerRef.current = 'runner'; setVanSelectedIdx(null) }}
              className={`px-2.5 py-1 font-medium transition-colors ${activeLayer === 'runner' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
            >Runner Route</button>
            <button
              onClick={() => { setActiveLayer('van'); activeLayerRef.current = 'van'; setSelectedIdx(null) }}
              className={`px-2.5 py-1 font-medium transition-colors border-l border-gray-300 ${activeLayer === 'van' ? 'bg-purple-700 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
            >Van Route</button>
          </div>

          <div className="w-px h-4 bg-gray-300" />

          {(['select', 'add', 'delete', 'lasso'] as const).map(m => (
            <button
              key={m}
              onClick={() => { setMode(m); modeRef.current = m; setSelectedIdx(null); setVanSelectedIdx(null) }}
              className={`px-3 py-1 text-xs rounded-md font-medium transition-colors ${
                mode === m
                  ? m === 'delete' || m === 'lasso' ? 'bg-red-600 text-white' : 'bg-indigo-600 text-white'
                  : 'bg-white border border-gray-300 text-gray-600 hover:bg-gray-50'
              }`}
            >
              {m === 'select' ? '↖ Select / Drag' : m === 'add' ? '+ Add point' : m === 'delete' ? '✕ Delete point' : '⬚ Lasso delete'}
            </button>
          ))}

          <button
            onClick={handleUndo}
            disabled={history.length === 0}
            title="Undo last edit (Ctrl+Z)"
            className="px-3 py-1 text-xs rounded-md font-medium bg-white border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-30"
          >↩ Undo{history.length > 0 ? ` (${history.length})` : ''}</button>

          <div className="ml-auto flex items-center gap-3">
            <button
              onClick={() => { setVanTransfer(v => !v); setUnsaved(true) }}
              title="Van transfer after this leg — end XP and next leg's start XP are different locations"
              className={`flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-md font-medium border transition-colors ${
                vanTransfer
                  ? 'bg-purple-700 text-white border-purple-700'
                  : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
              }`}
            >
              🚐 Van Transfer
            </button>
            <div className="w-px h-4 bg-gray-300" />
            <label className="text-xs text-gray-500 font-medium">Difficulty</label>
            <select
              value={difficultyRating}
              onChange={e => { setDifficultyRating(e.target.value); setUnsaved(true) }}
              className="text-xs border border-gray-300 rounded px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            >
              <option value="E">E — Easy</option>
              <option value="M">M — Moderate</option>
              <option value="MC">MC — More Challenging</option>
              <option value="MD">MD — Most Difficult</option>
            </select>
          </div>
        </div>

        {/* ── Mode hint ── */}
        <div className="px-3 py-0.5 text-xs text-gray-400 flex-shrink-0 bg-gray-50 border-b border-gray-100">
          {selectedXpLegNum !== null
            ? `XP ${selectedXpLegNum} selected — drag the marker to reposition, edit notes in the panel, then Save XP.`
            : mode === 'select' ? `Drag any ${activeLayer === 'van' ? 'van route' : 'runner route'} point to move it. Click an XP number to select it.`
            : mode === 'add' ? `Tap anywhere on the map to insert a point into the ${activeLayer === 'van' ? 'van route' : 'runner route'}.`
            : mode === 'lasso' ? 'Click and drag on the map to draw a selection box — all waypoints and XPs inside will be deleted.'
            : `Tap any intermediate ${activeLayer === 'van' ? 'van route' : 'runner route'} point to delete it.`}
        </div>

        {/* ── Leg & XP instructions ── */}
        <div className="px-3 py-1.5 flex-shrink-0 bg-white border-b border-gray-200 flex flex-col gap-1.5">
          <div className="flex items-center gap-2">
            <label className="text-xs font-semibold text-gray-500 whitespace-nowrap w-24">Leg Number</label>
            <input
              type="number"
              min={1}
              value={legNumberInput}
              onChange={e => { setLegNumberInput(e.target.value); setUnsaved(true) }}
              className="w-20 px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
            <span className="text-xs text-gray-400">(saved with route)</span>
          </div>
          <div className="flex items-start gap-2">
            <label className="text-xs font-semibold text-gray-500 whitespace-nowrap mt-1.5 w-24">Leg Notes</label>
            <textarea
              value={instructions}
              onChange={e => { setInstructions(e.target.value); setUnsaved(true) }}
              rows={2}
              placeholder="e.g. Reflective vest required · Headlamp required"
              className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 resize-none"
            />
          </div>
          <div className="flex items-start gap-2">
            <label className="text-xs font-semibold text-gray-500 whitespace-nowrap mt-1.5 w-24">XP {activeLeg ? activeLeg.legNumber : ''} Notes</label>
            <textarea
              value={xpInstructions}
              onChange={e => { setXpInstructions(e.target.value); setUnsaved(true) }}
              rows={2}
              placeholder="e.g. Toilets available · Parking on north side"
              className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 resize-none"
            />
          </div>
        </div>

        {/* ── Body: side panel + map ── */}
        <div className="flex-1 min-h-0 flex overflow-hidden">

          {/* Side panel */}
          {panelOpen && (
            <div className="w-36 flex-shrink-0 border-r border-gray-200 bg-white flex flex-col overflow-hidden">
              <div className="px-2 py-1.5 text-xs font-semibold text-gray-500 border-b border-gray-100 flex items-center justify-between">
                <span>All Legs</span>
                <span className="text-gray-400">{allLegs.length}</span>
              </div>
              <div className="flex-1 overflow-y-auto">
                {allLegs.map(leg => {
                  const isActive = leg.id === activeLegId
                  const hasWarning = alignmentWarnings.has(leg.legNumber)
                  return (
                    <div
                      key={leg.id}
                      className={`border-b border-gray-100 flex items-stretch transition-colors ${
                        isActive ? 'bg-indigo-50 border-l-2 border-l-indigo-500' : 'hover:bg-gray-50'
                      }`}
                    >
                      <button
                        onClick={() => handleSelectLeg(leg.id)}
                        className="flex-1 text-left px-2 py-1.5 flex items-start gap-1.5"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1 flex-wrap">
                            <span className={`text-xs font-bold ${isActive ? 'text-indigo-700' : 'text-gray-700'}`}>#{leg.legNumber}</span>
                            <DiffBadge d={leg.difficultyRating} />
                            <span className="text-xs font-mono text-gray-400">{leg.distanceMiles?.toFixed(1)}mi</span>
                            {hasWarning && <span className="text-amber-500 text-xs" title="Alignment gap">⚠</span>}
                            {leg.vanTransfer && <span className="text-purple-600 text-xs" title="Van transfer after this leg">🚐</span>}
                          </div>
                          <div className="text-xs mt-0.5">
                            <span className="text-green-600">↑{(leg.elevationGain ?? 0).toLocaleString()}</span>
                            {' '}<span className="text-orange-600">↓{(leg.elevationLoss ?? 0).toLocaleString()}</span>
                            <span className="text-gray-300"> ft</span>
                          </div>
                        </div>
                      </button>
                      {isActive && (
                        <button
                          onClick={() => handleDeleteXp(leg.legNumber)}
                          disabled={xpDeleting || allLegs.length <= 1}
                          title={`Delete Leg ${leg.legNumber}`}
                          className="px-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-30 flex-shrink-0 flex items-center"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {/* Map */}
          <div className="flex-1 min-w-0 relative">
            {legLoading && (
              <div className="absolute inset-0 bg-white bg-opacity-70 z-[2000] flex items-center justify-center">
                <div className="text-sm text-gray-500 font-medium">Loading leg…</div>
              </div>
            )}

            <MapContainer
              center={[startPt?.lat ?? 39.5, startPt?.lng ?? -119.8]}
              zoom={13}
              style={{ height: '100%', width: '100%' }}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                maxZoom={19}
              />
              <FitBounds pts={pts} trigger={fitTrigger} />
              <MapClickHandler onAdd={handleMapClick} enabled={mode === 'add'} />
              <LassoOverlay enabled={mode === 'lasso'} onSelect={handleLassoSelect} />

              {/* XP number markers — draggable, clickable to select */}
              {allLegs.filter(l => l.legNumber <= 35).map(l => {
                // Van transfer: this leg's end is XP Na, next leg's start is XP Nb
                const isVanEnd = l.vanTransfer
                const prevLegForXp = allLegs.find(p => p.legNumber === l.legNumber - 1)
                const isVanStart = prevLegForXp?.vanTransfer
                const endLabel = isVanEnd ? `${l.legNumber}a` : String(l.legNumber)
                return (
                  <Marker
                    key={`xp-end-${l.legNumber}`}
                    position={[l.endLat, l.endLng]}
                    icon={xpEditorIcon(endLabel, l.id === activeLegId, selectedXpLegNum === l.legNumber)}
                    draggable
                    zIndexOffset={500}
                    eventHandlers={{
                      click: (e) => { L.DomEvent.stopPropagation(e as any); handleXpClick(l.legNumber, l.xpInstructions ?? '') },
                      dragend: (e) => handleXpDrag(l.legNumber, e as L.DragEndEvent),
                    }}
                  />
                )
              })}
              {/* XP Nb markers — start of leg after a van transfer */}
              {allLegs.filter(l => {
                const prev = allLegs.find(p => p.legNumber === l.legNumber - 1)
                return prev?.vanTransfer
              }).map(l => {
                const prevLegNum = l.legNumber - 1
                const isSelected = selectedXpLegNum === -(l.legNumber) // negative = "b" side
                return (
                  <Marker
                    key={`xp-start-${l.legNumber}`}
                    position={[l.startLat, l.startLng]}
                    icon={xpEditorIcon(`${prevLegNum}b`, l.id === activeLegId, isSelected)}
                    draggable
                    zIndexOffset={500}
                    eventHandlers={{
                      click: (e) => { L.DomEvent.stopPropagation(e as any); handleXpClick(-(l.legNumber), l.xpInstructions ?? '') },
                      dragend: (e) => handleXpDragStart(l.legNumber, e as L.DragEndEvent),
                    }}
                  />
                )
              })}

              {/* Adjacent leg previews — solid orange */}
              {prevLegPts.length > 1 && (
                <Polyline positions={prevLegPts.map(p => [p.lat, p.lng])} color="#f97316" weight={4} opacity={1} />
              )}
              {nextLegPts.length > 1 && (
                <Polyline positions={nextLegPts.map(p => [p.lat, p.lng])} color="#f97316" weight={4} opacity={1} />
              )}

              {/* Adjacent leg endpoint markers */}
              {prevLeg && <Marker position={[prevLeg.endLat, prevLeg.endLng]} icon={adjacentIcon} />}
              {nextLeg && <Marker position={[nextLeg.startLat, nextLeg.startLng]} icon={adjacentIcon} />}

              {/* Van route — forest green, always solid */}
              {vanPts.length > 1 && (
                <Polyline
                  positions={vanPts.map(p => [p.lat, p.lng])}
                  color="#15803d"
                  weight={activeLayer === 'van' ? 5 : 3}
                  opacity={activeLayer === 'van' ? 1.0 : 0.6}
                />
              )}

              {/* Van route markers — only shown when van layer active */}
              {activeLayer === 'van' && vanPts.slice(1, -1).map((p, rawIdx) => {
                const idx = rawIdx + 1
                return (
                  <Marker
                    key={`van-${p.id}-${mode}`}
                    position={[p.lat, p.lng]}
                    icon={waypointIcon(vanSelectedIdx === idx)}
                    draggable={mode === 'select'}
                    eventHandlers={{
                      click: (e) => { L.DomEvent.stopPropagation(e as any); handleVanMarkerClick(idx) },
                      dragend: (e) => handleVanMarkerDrag(idx, e as L.DragEndEvent),
                    }}
                  />
                )
              })}
              {activeLayer === 'van' && vanPts[0] && (
                <Marker key={`van-start-${vanPts[0].id}-${mode}`} position={[vanPts[0].lat, vanPts[0].lng]} icon={startIcon} draggable
                  eventHandlers={{ click: () => setVanSelectedIdx(0), dragend: (e) => handleVanMarkerDrag(0, e as L.DragEndEvent) }} />
              )}
              {activeLayer === 'van' && vanPts.length > 1 && (
                <Marker key={`van-end-${vanPts[vanPts.length-1].id}-${mode}`} position={[vanPts[vanPts.length-1].lat, vanPts[vanPts.length-1].lng]} icon={endIcon} draggable
                  eventHandlers={{ click: () => setVanSelectedIdx(vanPts.length - 1), dragend: (e) => handleVanMarkerDrag(vanPts.length - 1, e as L.DragEndEvent) }} />
              )}

              {/* Runner route — royal blue */}
              {polyLine.length > 1 && (
                <Polyline positions={polyLine} color="#1d4ed8" weight={4} opacity={0.9} />
              )}

              {/* Intermediate waypoints — hidden when van layer is active */}
              {activeLayer === 'runner' && pts.slice(1, -1).map((p, rawIdx) => {
                const idx = rawIdx + 1
                return (
                  <Marker
                    key={`${p.id}-${mode}`}
                    position={[p.lat, p.lng]}
                    icon={waypointIcon(selectedIdx === idx)}
                    draggable={mode === 'select'}
                    eventHandlers={{
                      click: (e) => { L.DomEvent.stopPropagation(e as any); handleMarkerClick(idx) },
                      dragend: (e) => handleMarkerDrag(idx, e as L.DragEndEvent),
                    }}
                  />
                )
              })}

              {/* Start / end markers — hidden when van layer is active */}
              {activeLayer === 'runner' && startPt && (
                <Marker
                  key={`${startPt.id}-${mode}`}
                  position={[startPt.lat, startPt.lng]}
                  icon={startIcon}
                  draggable
                  eventHandlers={{
                    click: () => setSelectedIdx(0),
                    dragend: (e) => handleMarkerDrag(0, e as L.DragEndEvent),
                  }}
                />
              )}

              {activeLayer === 'runner' && endPt && pts.length > 1 && (
                <Marker
                  key={`${endPt.id}-${mode}`}
                  position={[endPt.lat, endPt.lng]}
                  icon={endIcon}
                  draggable
                  eventHandlers={{
                    click: () => setSelectedIdx(pts.length - 1),
                    dragend: (e) => handleMarkerDrag(pts.length - 1, e as L.DragEndEvent),
                  }}
                />
              )}
            </MapContainer>

            {/* Snap buttons */}
            <div className="absolute bottom-3 left-3 z-[1000] flex flex-col gap-1">
              {!startMatchesPrev && prevLeg && (
                <button
                  onClick={() => { setPts(prev => [newPt(prevLeg.endLat, prevLeg.endLng), ...prev.slice(1)]); setUnsaved(true) }}
                  className="bg-amber-500 text-white text-xs px-3 py-1.5 rounded-lg shadow font-medium hover:bg-amber-600"
                >Snap start → Leg {prevLeg.legNumber} end</button>
              )}
              {!endMatchesNext && nextLeg && (
                <button
                  onClick={() => { setPts(prev => [...prev.slice(0, -1), newPt(nextLeg.startLat, nextLeg.startLng)]); setUnsaved(true) }}
                  className="bg-amber-500 text-white text-xs px-3 py-1.5 rounded-lg shadow font-medium hover:bg-amber-600"
                >Snap end → Leg {nextLeg.legNumber} start</button>
              )}
            </div>

            {/* XP selected panel */}
            {selectedXpLegNum !== null && (() => {
              const isBSide = selectedXpLegNum < 0
              const absLegNum = Math.abs(selectedXpLegNum)
              const xpLeg = isBSide
                ? allLegs.find(l => l.legNumber === absLegNum)
                : allLegs.find(l => l.legNumber === selectedXpLegNum)
              if (!xpLeg) return null
              const prevForB = isBSide ? allLegs.find(l => l.legNumber === absLegNum - 1) : null
              const xpLabel = isBSide ? `${absLegNum - 1}b` : xpLeg.vanTransfer ? `${selectedXpLegNum}a` : String(selectedXpLegNum)
              const lat = isBSide ? xpLeg.startLat : xpLeg.endLat
              const lng = isBSide ? xpLeg.startLng : xpLeg.endLng
              const subtitle = isBSide
                ? `Van transfer — Leg ${absLegNum} start`
                : xpLeg.vanTransfer
                  ? `Van transfer — Leg ${selectedXpLegNum} end`
                  : `Leg ${selectedXpLegNum} end · Leg ${selectedXpLegNum + 1} start`
              return (
                <div className="absolute top-3 right-3 z-[1000] bg-white rounded-lg shadow-lg border border-amber-300 w-64">
                  <div className="flex items-center gap-2 px-3 py-2 border-b border-amber-100 bg-amber-50 rounded-t-lg">
                    <span className="text-sm font-bold text-amber-800">XP {xpLabel}</span>
                    <span className="text-xs text-amber-600 flex-1">{subtitle}</span>
                    <button onClick={() => setSelectedXpLegNum(null)} className="text-gray-400 hover:text-gray-600 text-xs">✕</button>
                  </div>
                  <div className="px-3 py-2 space-y-2">
                    <p className="text-xs text-gray-400 font-mono">
                      {lat.toFixed(6)}, {lng.toFixed(6)}
                    </p>
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 mb-1">XP Notes / Amenities</label>
                      <textarea
                        value={xpEditNote}
                        onChange={e => { setXpEditNote(e.target.value); setUnsaved(true) }}
                        rows={3}
                        placeholder="e.g. Toilets available · Parking on north side · Van drop zone"
                        className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-amber-500 resize-none"
                      />
                    </div>
                    <p className="text-xs text-gray-400">Drag the marker to move this XP. To delete this leg, select it in the leg list and click the trash icon.</p>
                    <div className="flex gap-1.5 pt-0.5">
                      <button
                        onClick={() => !isBSide && handleAddXp(selectedXpLegNum)}
                        disabled={xpAdding || xpSaving || isBSide}
                        title={`Insert a new XP after XP ${selectedXpLegNum}`}
                        className="px-2 py-1 bg-green-600 text-white text-xs rounded-md font-medium hover:bg-green-700 disabled:opacity-40 flex-shrink-0"
                      >{xpAdding ? '…' : '+ Add XP after'}</button>
                      <button
                        onClick={() => handleXpSave(selectedXpLegNum)}
                        disabled={xpSaving || xpAdding}
                        className="px-3 py-1 bg-amber-500 text-white text-xs rounded-md font-medium hover:bg-amber-600 disabled:opacity-50 flex-shrink-0 ml-auto"
                      >{xpSaving ? 'Saving…' : 'Save XP'}</button>
                    </div>
                  </div>
                </div>
              )
            })()}

            {/* Van selected point info */}
            {vanSelectedIdx !== null && vanPts[vanSelectedIdx] && (
              <div className="absolute top-3 left-3 z-[1000] bg-white rounded-lg shadow-lg border border-purple-200 px-3 py-2 text-xs">
                <p className="font-semibold text-purple-700 mb-0.5">
                  {vanSelectedIdx === 0 ? '🟢 Van Start' : vanSelectedIdx === vanPts.length - 1 ? '🔴 Van End' : `Van Point ${vanSelectedIdx}`}
                </p>
                <p className="font-mono text-gray-500">{vanPts[vanSelectedIdx].lat.toFixed(6)}, {vanPts[vanSelectedIdx].lng.toFixed(6)}</p>
                {vanSelectedIdx !== 0 && vanSelectedIdx !== vanPts.length - 1 && (
                  <button
                    onClick={() => { setVanPts(prev => prev.filter((_, i) => i !== vanSelectedIdx)); setVanSelectedIdx(null); setUnsaved(true) }}
                    className="mt-1 text-red-500 hover:underline"
                  >Delete this point</button>
                )}
              </div>
            )}

            {/* Selected point info */}
            {selectedIdx !== null && pts[selectedIdx] && (
              <div className="absolute top-3 left-3 z-[1000] bg-white rounded-lg shadow-lg border border-gray-200 px-3 py-2 text-xs">
                <p className="font-semibold text-gray-700 mb-0.5">
                  {selectedIdx === 0 ? '🟢 Start' : selectedIdx === pts.length - 1 ? '🔴 End' : `Point ${selectedIdx}`}
                </p>
                <p className="font-mono text-gray-500">{pts[selectedIdx].lat.toFixed(6)}, {pts[selectedIdx].lng.toFixed(6)}</p>
                {pts[selectedIdx].elevM !== undefined && (
                  <p className="text-gray-400">{(pts[selectedIdx].elevM! * 3.28084).toFixed(0)} ft elevation</p>
                )}
                {selectedIdx !== 0 && selectedIdx !== pts.length - 1 && (
                  <button
                    onClick={() => { setPts(prev => prev.filter((_, i) => i !== selectedIdx)); setSelectedIdx(null); setUnsaved(true) }}
                    className="mt-1 text-red-500 hover:underline"
                  >Delete this point</button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* ── Legend ── */}
        <div className="flex-shrink-0 bg-white border-t border-gray-200 px-3 py-1.5 flex items-center gap-4 text-xs text-gray-500">
          <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-full bg-green-500 border border-white" />Start</span>
          <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-full bg-red-500 border border-white" />End</span>
          <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-full bg-blue-700 border border-white" />Runner route</span>
          <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-full bg-green-700 border border-white" />Van route</span>
          <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-full bg-indigo-500 border border-white" />Waypoint</span>
          <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-full bg-orange-500 border border-white" />Adjacent leg</span>
          <span className="ml-auto font-mono font-semibold text-gray-700">
            {distMi.toFixed(2)} mi · {pts.length} pts · {allLegs.length} XPs
            {missingElev > 0 && <span className="ml-1 text-amber-500 font-normal">· {missingElev} missing elev</span>}
          </span>
        </div>
      </div>
    </AppShell>
  )
}
